import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { NotificationBell } from './NotificationBell';
import { ReminderToasts } from './ReminderToasts';
import { BlockedToasts } from './BlockedToasts';
import { CalendarReminderToasts } from './CalendarReminderToasts';
import { EnablePushPrompt } from './EnablePushPrompt';
import { AppFooter } from './AppFooter';
import { ModulePicker, pickerAlreadyShown } from './ModulePicker';
import { TaskViewerProvider } from '../hooks/useTaskViewer';
import { useProfileRefreshOnFocus } from '../hooks/useProfileRefreshOnFocus';
import { useAuth } from '../hooks/useAuth';
import { MODULES, moduleForPath, visibleModuleItems } from '../lib/modules';

/**
 * The authenticated shell: sidebar on the left, a slim top bar carrying the
 * notification bell, the routed page below it, and a permanent footer bar
 * across the bottom.
 *
 * The footer is a LAYOUT row, not an overlay: the sidebar-and-page row and
 * the footer are stacked in a column, so the bar takes its own height and
 * never covers the page the way a floating button did.
 *
 * Mounted as a layout route, so the sidebar and the bell render once and
 * survive navigation - which is what lets the bell keep its unread count
 * without refetching on every page change.
 */
export function AppLayout() {
  // A permission granted while the tab was in the background (e.g. the
  // Lottery section) shows when the person comes back - at most every 5 min.
  useProfileRefreshOnFocus();

  /**
   * The burger's state: FULL sidebar vs the ICON RAIL. The sidebar is never
   * gone - contracted, it becomes a slim strip of icons so every module
   * stays one tap away even on a phone. Starts expanded on desktop widths
   * and as the rail on small screens. Expanded below lg floats as a drawer
   * over the page (with a backdrop) instead of squeezing the board.
   */
  /**
   * The module picker, once per sign-in. Starts open unless this session
   * has already answered it - see ModulePicker for why it is a session and
   * not a stored preference.
   */
  const [picking, setPicking] = useState(() => !pickerAlreadyShown());

  /*
   * The burger toggles the MODULE PANEL, so it only means anything when the
   * active module has one. CG Sign, Meetings, CG Vault and Audit Trail are
   * each a single screen - a burger there toggles nothing, and a control
   * that does nothing is worse than no control.
   */
  const { pathname } = useLocation();
  const { user } = useAuth();
  const active = MODULES.find((m) => m.id === moduleForPath(pathname));
  const hasPanel =
    visibleModuleItems(active?.items, user?.role.systemKey, user?.permissions ?? []).length > 0;

  const [expanded, setExpanded] = useState<boolean>(() =>
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 1024px)').matches
      : true,
  );

  return (
    <TaskViewerProvider>
      <div className="flex h-full flex-col bg-white">
        <div className="flex min-h-0 flex-1">
          {expanded && hasPanel ? (
            <>
              {/* Mobile-only backdrop: tap outside the drawer to contract it. */}
              <div
                aria-hidden="true"
                onClick={() => setExpanded(false)}
                className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
              />
              <div className="fixed inset-y-0 left-0 z-40 shadow-xl lg:static lg:z-auto lg:shadow-none">
                <Sidebar
                  onNavigate={() => {
                    // A drawer should get out of the way once a page is picked;
                    // the desktop column stays put.
                    if (!window.matchMedia('(min-width: 1024px)').matches) {
                      setExpanded(false);
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <Sidebar collapsed />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <header className="flex h-12 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4">
              {hasPanel ? (
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-label={expanded ? 'Contract the menu' : 'Expand the menu'}
                aria-expanded={expanded}
                className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5">
                  <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
              ) : (
                /* Keeps the bell on the right where it always is. */
                <span />
              )}
              <NotificationBell />
            </header>
            <main className="flex-1 overflow-y-auto bg-slate-50/60">
              <Outlet />
            </main>
            {/* ONE shared bottom-right stack. Each toast group used to pin
                itself fixed at bottom-4 right-4, so two groups firing at once
                rendered on top of each other; now the wrapper owns the fixed
                position and the groups stack vertically inside it.
                bottom-14 clears the footer bar. */}
            <div className="pointer-events-none fixed bottom-14 right-4 z-50 flex w-80 flex-col gap-2">
              <EnablePushPrompt />
              <ReminderToasts />
              <BlockedToasts />
              <CalendarReminderToasts />
            </div>
          </div>
        </div>
        <AppFooter />
      </div>
      {picking && <ModulePicker onPick={() => setPicking(false)} />}
    </TaskViewerProvider>
  );
}
